/**
 * Reach input (M3 plan §2.6.6, §2.10): reads M1's `InputDevices` (held keys and the polled pad) with M3's own
 * bindings (`reach-controls.json`) into one `ReachInputState` per tick. Edges are taken against the state's own
 * previous values, so nothing here touches M1's edge actions. Allocation-free (D-014).
 *
 * - Keyboard: holds and rising/falling edges from `input.keys`.
 * - Pad: the L3 + R3 chord for cruise, X held for scan, D-pad down held 0.5 s for interact and View held 0.5 s for
 *   the map (a tap keeps its M1 or M2 meaning), and left stick Y for the cruise throttle.
 */
import type { InputDevices } from '../input/devices'
import type { PadLike } from '../input/playerInput'
import type { ReachControls } from './controls'

export interface ReachInputState {
  cruiseHeld: boolean; cruisePressed: boolean; cruiseReleased: boolean
  scanHeld: boolean; interactPressed: boolean
  throttleAxis: number // −1..1 (W/S or left stick Y); only read while cruising
  /** Additive (R1): G, or View held `gamepad.map.holdS` (a rising edge). The maps feature may read its own edges instead. */
  mapPressed: boolean
  /** Additive (R1): `readReachInput`'s own previous state (key levels and pad hold timers). */
  readonly prev: ReachInputPrev
}

export interface ReachInputPrev {
  /** The cruise level (key or chord) last read, for the press and release edges. */
  cruise: boolean
  interactKey: boolean
  mapKey: boolean
  /** Seconds the pad button has been held (0 while up). */
  interactPadS: number
  mapPadS: number
}

export function createReachInput(): ReachInputState {
  return {
    cruiseHeld: false,
    cruisePressed: false,
    cruiseReleased: false,
    scanHeld: false,
    interactPressed: false,
    throttleAxis: 0,
    mapPressed: false,
    prev: { cruise: false, interactKey: false, mapKey: false, interactPadS: 0, mapPadS: 0 },
  }
}

/** Float slack so N ticks of 1/60 s reach a hold threshold of N/60 s exactly. */
const HOLD_EPS = 1e-6

/** Seconds a button has been held after this step (0 once it is up). */
export function holdTime(prevS: number, down: boolean, dt: number): number {
  return down ? prevS + dt : 0
}

/** True on the one step where a hold first reaches `holdS`. */
export function holdReached(prevS: number, nowS: number, holdS: number): boolean {
  return prevS < holdS - HOLD_EPS && nowS >= holdS - HOLD_EPS
}

/** True when any of `codes` is held. */
export function anyKeyHeld(keys: ReadonlySet<string>, codes: readonly string[]): boolean {
  for (let i = 0; i < codes.length; i++) if (keys.has(codes[i])) return true
  return false
}

/** True when pad button `index` is pressed. */
export function padPressed(pad: PadLike | null, index: number): boolean {
  return pad !== null && pad.buttons[index]?.pressed === true
}

/** True when every button of the chord is pressed. */
export function padChord(pad: PadLike | null, buttons: readonly number[]): boolean {
  if (pad === null || buttons.length === 0) return false
  for (let i = 0; i < buttons.length; i++) if (pad.buttons[buttons[i]]?.pressed !== true) return false
  return true
}

/** Reads held keys (`input.keys`) and pad buttons into `out`, with edges against its own previous state. Allocation-free. */
export function readReachInput(input: InputDevices, controls: ReachControls, dt: number, out: ReachInputState): void {
  const keys = input.keys
  const pad = input.pad
  const k = controls.keyboard
  const g = controls.gamepad
  const prev = out.prev

  const cruise = anyKeyHeld(keys, k.cruise) || padChord(pad, g.cruise.buttons)
  out.cruisePressed = cruise && !prev.cruise
  out.cruiseReleased = !cruise && prev.cruise
  out.cruiseHeld = cruise
  prev.cruise = cruise

  out.scanHeld = anyKeyHeld(keys, k.scan) || padPressed(pad, g.scan.button)

  const interactKey = anyKeyHeld(keys, k.interact)
  const interactS = holdTime(prev.interactPadS, padPressed(pad, g.interact.button), dt)
  out.interactPressed = (interactKey && !prev.interactKey) || holdReached(prev.interactPadS, interactS, g.interact.holdS)
  prev.interactKey = interactKey
  prev.interactPadS = interactS

  const mapKey = anyKeyHeld(keys, k.map)
  const mapS = holdTime(prev.mapPadS, padPressed(pad, g.map.button), dt)
  out.mapPressed = (mapKey && !prev.mapKey) || holdReached(prev.mapPadS, mapS, g.map.holdS)
  prev.mapKey = mapKey
  prev.mapPadS = mapS

  let throttle = (anyKeyHeld(keys, k.throttleUp) ? 1 : 0) - (anyKeyHeld(keys, k.throttleDown) ? 1 : 0)
  if (pad !== null) {
    // Standard mapping: stick Y is negative when pushed up, and up means more throttle.
    const v = -(pad.axes[g.throttleAxis] ?? 0)
    if (v > g.throttleDeadzone || v < -g.throttleDeadzone) throttle += v
  }
  out.throttleAxis = throttle > 1 ? 1 : throttle < -1 ? -1 : throttle
}

/**
 * For ticks where reach input must not count (a modal is open): every output is zeroed, and whatever is held now is
 * remembered as already held (pad holds as already fired), so nothing held through the modal fires a press or a
 * release when reading resumes. Allocation-free.
 */
export function suppressReachInput(input: InputDevices, controls: ReachControls, out: ReachInputState): void {
  const keys = input.keys
  const pad = input.pad
  const k = controls.keyboard
  const g = controls.gamepad
  const prev = out.prev
  out.cruiseHeld = false
  out.cruisePressed = false
  out.cruiseReleased = false
  out.scanHeld = false
  out.interactPressed = false
  out.mapPressed = false
  out.throttleAxis = 0
  prev.cruise = anyKeyHeld(keys, k.cruise) || padChord(pad, g.cruise.buttons)
  prev.interactKey = anyKeyHeld(keys, k.interact)
  prev.mapKey = anyKeyHeld(keys, k.map)
  prev.interactPadS = padPressed(pad, g.interact.button) ? g.interact.holdS : 0
  prev.mapPadS = padPressed(pad, g.map.button) ? g.map.holdS : 0
}
